<script>
// @ts-check

import { getActiveDimensions, getDimensions } from '@/api/reportingDimensions'
import { getBalanceSheetReport } from '@/api/reports'
import BaseDateRange from '@/components/base/BaseDateRange.vue'
import BaseSelect from '@/components/base/BaseSelect.vue'
import LoadingSpinner from '@/components/base/LoadingSpinner.vue'
import FileViewer from '@/components/file/FileViewer.vue'
import { demoMixin } from '@/core/mixins/demo'
import {
  formattedDimension,
  formattedPeriod,
  formattedTag,
  toCamelCase
} from '@/helpers/formatters'
import { downloadFile } from '@/helpers/functions'
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'BalanceSheet',
  components: {
    FileViewer,
    LoadingSpinner,
    BaseDateRange,
    BaseSelect
  },
  mixins: [demoMixin],
  data: () => ({
    accountingPeriodId: null,
    periodData: null,
    report: null,
    isLoadingPeriods: false,
    isLoadingReport: false,
    startDate: null,
    endDate: null,
    dimension: null,
    dimensions: [],
    tag: null,
    tags: []
  }),
  computed: {
    ...mapGetters('reports', ['accountingPeriods']),
    ...mapGetters('mobileData', ['isMobileWrapperUser']),
    ...mapGetters('app', ['isOyService']),
    formattedAccountingPeriodOptions() {
      return this.accountingPeriods.map(formattedPeriod)
    },
    formattedDimensions() {
      return [
        {
          label: this.$t('reporting_dimensions.no_selection'),
          value: null
        },
        ...this.dimensions.map(formattedDimension)
      ]
    },
    formattedTags({ tags, dimension }) {
      const options = [
        {
          label: this.$t('reporting_dimensions.no_selection'),
          value: null
        }
      ]

      if (!dimension) {
        return options
      }

      return [
        ...options,
        ...tags.data
          .filter(({ dimensionType }) => dimensionType === dimension)
          .map(formattedTag)
      ]
    },
    dateRange: {
      get() {
        return [this.startDate, this.endDate]
      },
      set([startDate, endDate]) {
        this.startDate = startDate
        this.endDate = endDate
      }
    },
    refetchWatcher({ dateRange, tag, dimension }) {
      return {
        tag,
        dimension,
        dateRange
      }
    }
  },
  watch: {
    accountingPeriodId(newPeriod) {
      if (!newPeriod) {
        return
      }

      this.periodData = this.accountingPeriods.find(
        ({ id }) => `${id}` === `${this.accountingPeriodId}`
      )

      this.dateRange = [
        this.periodData.period_start,
        this.periodData.period_end
      ]
    },
    async refetchWatcher() {
      if (this.startDate && this.endDate) {
        await this.skipInDemoMode(this.getBalanceSheetReport)
      } else {
        this.report = null
      }

      if (
        this.periodData &&
        (this.periodData.period_start !== this.startDate ||
          this.periodData.period_end !== this.endDate)
      ) {
        this.accountingPeriodId = null
        this.periodData = null
      }

      this.isLoadingPeriods = false
    }
  },
  async created() {
    this.isLoadingPeriods = true

    await this.skipInDemoMode(this.fetchAccountingPeriods)

    if (this.isOyService) {
      ;[this.dimensions, this.tags] = toCamelCase(
        await Promise.all([this.fetchDimensions(), this.fetchTags()])
      )
    }

    this.isLoadingPeriods = false
  },
  methods: {
    ...mapActions('reports', ['getAccountingPeriods']),
    async fetchDimensions() {
      return getActiveDimensions()
    },
    async fetchTags() {
      return getDimensions()
    },
    async fetchAccountingPeriods() {
      try {
        await this.getAccountingPeriods()

        const accountingPeriod = this.accountingPeriods.find(
          ({ current }) => current === true
        )

        if (accountingPeriod) {
          this.accountingPeriodId = accountingPeriod.id
        }
      } catch (error) {
        console.error(error)
      }
    },
    downloadReport() {
      if (!this.report) {
        return
      }

      downloadFile(
        this.report.file,
        `${this.startDate.replace(/-/g, '')}_balance_sheet.pdf`,
        'application/pdf',
        this.isMobileWrapperUser
      )
    },
    async getBalanceSheetReport() {
      this.report = null
      this.isLoadingReport = true

      try {
        this.report = await getBalanceSheetReport(
          this.startDate,
          this.endDate,
          null,
          true,
          this.dimension,
          this.tag
        )
      } catch (error) {
        console.error(error)
      }

      this.isLoadingReport = false
    }
  }
}
</script>

<template>
  <div v-if="isLoadingPeriods" class="my-4 flex items-center justify-center">
    <p>{{ $t('reports.loading_account_periods') }}</p>
    <LoadingSpinner class="ml-4" />
  </div>

  <div v-else>
    <p class="mb-4">
      {{ $t('reports.only_shows_accepted_entries') }}
    </p>

    <div class="flex flex-col sm:flex-row sm:gap-x-4">
      <BaseSelect
        v-model="accountingPeriodId"
        :label="$t('form.label.accounting_period')"
        :placeholder="$t('reports.choose_accounting_period')"
        :options="formattedAccountingPeriodOptions"
        class="sm:w-1/2"
      />
      <BaseDateRange
        v-model="dateRange"
        :label="$t('reports.get_daterange')"
        class="sm:w-1/2"
      />
    </div>

    <div v-if="isOyService" class="flex flex-col sm:flex-row sm:gap-x-4">
      <BaseSelect
        v-model="dimension"
        :label="$t('reporting_dimensions.form.dimension')"
        :placeholder="$t('reports.choose_dimension')"
        :options="formattedDimensions"
        class="sm:w-1/2"
      />
      <BaseSelect
        v-model="tag"
        :label="$t('reporting_dimensions.form.tag')"
        :placeholder="$t('reports.choose_tag')"
        :options="formattedTags"
        class="sm:w-1/2"
      />
    </div>

    <FileViewer
      v-if="report && !isInDemoMode"
      class="my-4 w-full sm:mt-0"
      :file="report"
    />

    <div
      v-else-if="isLoadingReport"
      class="my-8 flex items-center justify-center"
    >
      <p>{{ $t('reports.loading_balance_sheet') }}</p>
      <LoadingSpinner class="ml-4" />
    </div>

    <p v-else-if="!isInDemoMode" class="my-8 flex items-center justify-center">
      {{ $t('reports.select_balance_sheet_date_range') }}
    </p>

    <div v-if="!isInDemoMode" class="justify-end sm:flex">
      <PrimaryButton
        v-show="report"
        class="w-full lg:w-auto"
        :text="$t('reports.download_balance_sheet')"
        @click="downloadReport"
      />
    </div>
  </div>
</template>
